/* eslint-disable @typescript-eslint/no-unused-vars */
/* ─── Planner-paneel: kanban-bord met taken, buckets en drag & drop ──────── */
import { useState, useEffect, useRef } from "react";
import type { CheckItem, PlannerTask, Bucket, Project } from "./types";
import { gQ } from "./graphApi";
import { MAX_CHECKLIST_ITEMS } from "./config";
import { I, prioColor, buildOrderHint, newChecklistId, Checkbox } from "./shared";

function TaskCard({task,color,done,graphClient,onRefresh,onToggleDone}:{task:PlannerTask;color:string;done:boolean;graphClient:any;onRefresh:()=>void;onToggleDone?:(taskId:string,done:boolean)=>void}){
  const[localDone,setLocalDone]=useState(done);
  const[localTitle,setLocalTitle]=useState(task.title);
  const[localChecklist,setLocalChecklist]=useState(task.checklist);
  const[hover,setHover]=useState(false);
  const[adding,setAdding]=useState(false);     // tekstveld voor nieuw controlepunt zichtbaar?
  const[newItem,setNewItem]=useState("");       // tekst van nieuw controlepunt
  const[saving,setSaving]=useState(false);      // bezig met opslaan?
  const[editingTitle,setEditingTitle]=useState(false);     // taaktitel in bewerkmodus?
  const[titleDraft,setTitleDraft]=useState("");
  const[editingItemId,setEditingItemId]=useState<string|null>(null);  // welk controlepunt wordt bewerkt
  const[itemDraft,setItemDraft]=useState("");

  // Etag-cache: na elke succesvolle PATCH bewaren we de nieuwe etag.
  // Hierdoor hoeven we niet voor elke wijziging eerst een GET te doen — dat halveert het aantal API-calls.
  // Graph stuurt de nieuwe etag terug in de response van een PATCH.
  const taskEtagRef=useRef<string|null>(null);
  const detailsEtagRef=useRef<string|null>(null);

  // Sync met props als parent refresht
  useEffect(()=>{setLocalDone(done)},[done]);
  useEffect(()=>{setLocalTitle(task.title)},[task.title]);
  useEffect(()=>{
    setLocalChecklist(task.checklist);
    // Bij prop-update is onze gecachede details-etag mogelijk verouderd
    detailsEtagRef.current=null;
  },[task.checklist]);

  const od=!localDone&&task.dueDateTime&&new Date(task.dueDateTime.dateTime)<new Date();
  const uc=localChecklist.filter(x=>!x.isChecked),ck=localChecklist.filter(x=>x.isChecked);
  const atLimit=localChecklist.length>=MAX_CHECKLIST_ITEMS;

  // Helper: haal etag op (uit cache of via GET) en doe een PATCH op de taak.
  // Bij 412 (etag verlopen) doen we één GET en proberen opnieuw.
  // Geen onRefresh — de optimistic UI heeft de wijziging al, en de server is ook bij.
  const patchTask=async(payload:any):Promise<void>=>{
    let etag=taskEtagRef.current;
    if(!etag){
      const d=await gQ(()=>graphClient.api(`/planner/tasks/${task.id}`).get());
      etag=d["@odata.etag"];
    }
    try{
      // Graph PATCH retourneert geen body bij Planner, maar etag verandert wel.
      // We invalideren de cache zodat de volgende call vers ophaalt.
      await gQ(()=>graphClient.api(`/planner/tasks/${task.id}`).header("If-Match",etag!).patch(payload));
      taskEtagRef.current=null; // forceer verse etag voor volgende wijziging
    }catch(e:any){
      if(e?.statusCode===412||e?.status===412){
        // Cache was stale. Eén keer opnieuw met verse etag.
        const d=await gQ(()=>graphClient.api(`/planner/tasks/${task.id}`).get());
        await gQ(()=>graphClient.api(`/planner/tasks/${task.id}`).header("If-Match",d["@odata.etag"]).patch(payload));
        taskEtagRef.current=null;
        return;
      }
      throw e;
    }
  };

  const patchDetails=async(payload:any):Promise<void>=>{
    let etag=detailsEtagRef.current;
    if(!etag){
      const d=await gQ(()=>graphClient.api(`/planner/tasks/${task.id}/details`).get());
      etag=d["@odata.etag"];
    }
    try{
      await gQ(()=>graphClient.api(`/planner/tasks/${task.id}/details`).header("If-Match",etag!).patch(payload));
      detailsEtagRef.current=null;
    }catch(e:any){
      if(e?.statusCode===412||e?.status===412){
        const d=await gQ(()=>graphClient.api(`/planner/tasks/${task.id}/details`).get());
        await gQ(()=>graphClient.api(`/planner/tasks/${task.id}/details`).header("If-Match",d["@odata.etag"]).patch(payload));
        detailsEtagRef.current=null;
        return;
      }
      throw e;
    }
  };

  const tc=async()=>{
    const newDone=!localDone;
    setLocalDone(newDone); // optimistisch
    // Geef het direct door aan het bord: de taak verhuist meteen naar
    // (of uit) het ingeklapte groepje "Voltooide taken" en verdwijnt
    // dus uit de actieve kolom in plaats van vervaagd te blijven staan.
    if(onToggleDone)onToggleDone(task.id,newDone);
    try{
      await patchTask({percentComplete:newDone?100:0});
      // Geen onRefresh: de optimistic state is correct en de server is bij.
    }catch(e){
      console.error(e);
      setLocalDone(!newDone); // terugdraaien bij fout
      if(onToggleDone)onToggleDone(task.id,!newDone);
    }
  };

  const tci=async(iid:string,cur:boolean)=>{
    // Optimistisch: vink het item direct af in lokale state
    setLocalChecklist(prev=>prev.map(x=>x.id===iid?{...x,isChecked:!cur}:x));
    try{
      const p:any={checklist:{}};p.checklist[iid]={"@odata.type":"#microsoft.graph.plannerChecklistItem",isChecked:!cur};
      await patchDetails(p);
      // Geen onRefresh.
    }catch(e){
      console.error(e);
      setLocalChecklist(prev=>prev.map(x=>x.id===iid?{...x,isChecked:cur}:x)); // terugdraaien
    }
  };

  // Nieuw controlepunt toevoegen
  const addItem=async()=>{
    const text=newItem.trim();
    if(!text||saving||atLimit)return;
    const tempId=newChecklistId();
    const tempItem:CheckItem={id:tempId,title:text,isChecked:false};
    // Optimistisch: voeg direct toe aan UI
    setLocalChecklist(prev=>[...prev,tempItem]);
    setNewItem("");
    setAdding(false);
    setSaving(true);
    try{
      const p:any={checklist:{}};
      p.checklist[tempId]={"@odata.type":"#microsoft.graph.plannerChecklistItem",title:text,isChecked:false};
      await patchDetails(p);
      // Geen onRefresh.
    }catch(e){
      console.error("Controlepunt toevoegen mislukt:",e);
      // Terugdraaien bij fout
      setLocalChecklist(prev=>prev.filter(x=>x.id!==tempId));
    }
    setSaving(false);
  };

  // Taaktitel opslaan
  const saveTitle=async()=>{
    const text=titleDraft.trim();
    setEditingTitle(false);
    if(!text||text===localTitle)return;
    const oldTitle=localTitle;
    setLocalTitle(text); // optimistisch
    try{
      await patchTask({title:text});
    }catch(e){
      console.error("Titel wijzigen mislukt:",e);
      setLocalTitle(oldTitle); // terugdraaien
    }
  };

  // Controlepunt-tekst opslaan
  const saveItemTitle=async(iid:string)=>{
    const text=itemDraft.trim();
    const cur=localChecklist.find(x=>x.id===iid);
    setEditingItemId(null);
    if(!text||!cur||text===cur.title)return;
    const oldTitle=cur.title;
    setLocalChecklist(prev=>prev.map(x=>x.id===iid?{...x,title:text}:x));
    try{
      const p:any={checklist:{}};
      p.checklist[iid]={"@odata.type":"#microsoft.graph.plannerChecklistItem",title:text};
      await patchDetails(p);
    }catch(e){
      console.error("Controlepunt wijzigen mislukt:",e);
      setLocalChecklist(prev=>prev.map(x=>x.id===iid?{...x,title:oldTitle}:x)); // terugdraaien
    }
  };

  return <div
    onMouseEnter={()=>setHover(true)}
    onMouseLeave={()=>setHover(false)}
    style={{background:"#fff",borderRadius:6,padding:"8px 10px",border:"1px solid #E0E0E0",borderLeft:`3px solid ${localDone?"#CCC":prioColor(task.priority)}`,opacity:localDone?.5:1}}
  >
    {/* ── Taakkop met checkbox + titel + potlood ── */}
    <div style={{display:"flex",alignItems:"flex-start",gap:8}}>
      <div style={{marginTop:1}}><Checkbox checked={localDone} onChange={tc} size={18}/></div>
      {editingTitle
        ?<input
          autoFocus
          value={titleDraft}
          onChange={e=>setTitleDraft(e.target.value)}
          onKeyDown={e=>{
            if(e.key==="Enter"){e.preventDefault();saveTitle();}
            if(e.key==="Escape"){setEditingTitle(false);}
          }}
          onBlur={()=>saveTitle()}
          onClick={e=>e.stopPropagation()}
          // Drag-events blokkeren zodat slepen van de taak niet activeert tijdens typen
          onMouseDown={e=>e.stopPropagation()}
          style={{flex:1,fontSize:12,fontWeight:600,lineHeight:1.4,color:"#1A1A1A",border:"none",borderBottom:`1px solid ${color}`,outline:"none",background:"transparent",fontFamily:"Segoe UI",padding:"1px 2px"}}
        />
        :<span style={{fontSize:12,fontWeight:600,lineHeight:1.4,textDecoration:localDone?"line-through":"none",color:localDone?"#9AA0A6":"#1A1A1A",flex:1,wordBreak:"break-word"}}>{localTitle}</span>
      }
      {/* Potlood: alleen tonen bij hover, niet als al in edit-mode, niet bij voltooide taak */}
      {!localDone&&!editingTitle&&hover&&<button
        onClick={e=>{e.stopPropagation();setTitleDraft(localTitle);setEditingTitle(true);}}
        onMouseDown={e=>e.stopPropagation()}
        title="Taaknaam wijzigen"
        style={{display:"flex",alignItems:"center",justifyContent:"center",background:"none",border:"none",padding:2,cursor:"pointer",color:"#9AA0A6",flexShrink:0}}
        onMouseEnter={e=>e.currentTarget.style.color=color}
        onMouseLeave={e=>e.currentTarget.style.color="#9AA0A6"}
      ><I.Edit/></button>}
    </div>

    {/* ── Onafgevinkte controlepunten ── */}
    {!localDone&&uc.length>0&&<div style={{marginTop:5,marginLeft:26,display:"flex",flexDirection:"column",gap:3}}>
      {uc.map(item=>(
        <div key={item.id} style={{display:"flex",alignItems:"flex-start",gap:6,fontSize:11,color:"#5F6368"}}>
          <div style={{marginTop:1}}><Checkbox checked={false} onChange={()=>tci(item.id,false)} size={14}/></div>
          {editingItemId===item.id
            ?<input
              autoFocus
              value={itemDraft}
              onChange={e=>setItemDraft(e.target.value)}
              onKeyDown={e=>{
                if(e.key==="Enter"){e.preventDefault();saveItemTitle(item.id);}
                if(e.key==="Escape"){setEditingItemId(null);}
              }}
              onBlur={()=>saveItemTitle(item.id)}
              onClick={e=>e.stopPropagation()}
              onMouseDown={e=>e.stopPropagation()}
              style={{flex:1,fontSize:11,color:"#1A1A1A",border:"none",borderBottom:`1px solid ${color}`,outline:"none",background:"transparent",fontFamily:"Segoe UI",padding:"1px 2px"}}
            />
            :<span style={{flex:1,wordBreak:"break-word"}}>{item.title}</span>
          }
          {/* Potlood per controlepunt: alleen bij hover op de tegel én niet in edit-mode */}
          {hover&&editingItemId!==item.id&&<button
            onClick={e=>{e.stopPropagation();setItemDraft(item.title);setEditingItemId(item.id);}}
            onMouseDown={e=>e.stopPropagation()}
            title="Controlepunt wijzigen"
            style={{display:"flex",alignItems:"center",justifyContent:"center",background:"none",border:"none",padding:1,cursor:"pointer",color:"#9AA0A6",flexShrink:0,opacity:.7}}
            onMouseEnter={e=>e.currentTarget.style.color=color}
            onMouseLeave={e=>e.currentTarget.style.color="#9AA0A6"}
          ><I.Edit/></button>}
        </div>
      ))}
    </div>}

    {/* ── Inline tekstveld voor nieuw controlepunt ── */}
    {!localDone&&adding&&!atLimit&&<div style={{marginTop:5,marginLeft:26,display:"flex",alignItems:"center",gap:6}}>
      <div style={{width:14,height:14,borderRadius:"50%",border:"1.5px dashed #BBB",flexShrink:0}}/>
      <input
        autoFocus
        value={newItem}
        onChange={e=>setNewItem(e.target.value)}
        onKeyDown={e=>{
          if(e.key==="Enter"){e.preventDefault();addItem();}
          if(e.key==="Escape"){setAdding(false);setNewItem("");}
        }}
        onBlur={()=>{
          // Bij verlies focus: opslaan als er tekst is, anders sluiten
          if(newItem.trim())addItem();
          else setAdding(false);
        }}
        onMouseDown={e=>e.stopPropagation()}
        placeholder="Controlepunt..."
        style={{flex:1,border:"none",borderBottom:`1px solid ${color}`,outline:"none",fontSize:11,background:"transparent",color:"#1A1A1A",fontFamily:"Segoe UI",padding:"1px 2px"}}
      />
    </div>}

    {/* ── Plusje: alleen tonen bij hover, niet als veld al open is, niet bij voltooide taak, niet bij limiet ── */}
    {!localDone&&!adding&&hover&&!atLimit&&<button
      onClick={e=>{e.stopPropagation();setAdding(true);}}
      onMouseDown={e=>e.stopPropagation()}
      style={{marginTop:5,marginLeft:26,display:"flex",alignItems:"center",gap:5,fontSize:10,color:"#9AA0A6",background:"none",border:"none",padding:"2px 0",cursor:"pointer",fontFamily:"Segoe UI"}}
      onMouseEnter={e=>e.currentTarget.style.color=color}
      onMouseLeave={e=>e.currentTarget.style.color="#9AA0A6"}
    >
      <I.Plus/> Controlepunt toevoegen
    </button>}

    <div style={{display:"flex",alignItems:"center",gap:8,marginTop:6,marginLeft:26,flexWrap:"wrap"}}>
      {task.dueDateTime&&<span style={{fontSize:10,display:"flex",alignItems:"center",gap:2,color:od?"#D13438":"#9AA0A6",fontWeight:od?600:400}}><I.Clock/>{new Date(task.dueDateTime.dateTime).toLocaleDateString("nl-NL",{day:"numeric",month:"short"})}</span>}
      {localChecklist.length>0&&<span style={{fontSize:10,color:"#9AA0A6",display:"flex",alignItems:"center",gap:2}}><I.Check/>{ck.length}/{localChecklist.length}{atLimit?" (max)":""}</span>}
    </div>
  </div>
}

/* ─── Planner Board ──────────────────────────────────────────────────────── */
export function PlannerBoard({project,graphClient,onRefresh}:{project:Project;graphClient:any;onRefresh:()=>void}){
  const[dt,sDt]=useState<PlannerTask|null>(null);  // taak die gesleept wordt
  const[dob,sDob]=useState<string|null>(null);      // bucket-id als drop-target voor taak
  const[mv,sMv]=useState(false);                    // bezig met API patch
  const[ad,sAd]=useState<string|null>(null);const[nt,sNt]=useState("");const[al,sAl]=useState(false);
  const[sc,sSc]=useState<Record<string,boolean>>({});
  const[db,sDb]=useState<string|null>(null);        // bucket-id die gesleept wordt
  const[bo,sBo]=useState<string[]>([]);

  // Ref om dt synchroon te lezen in drag-handlers (state is stale in closures)
  const dtRef=useRef<PlannerTask|null>(null);
  const dbRef=useRef<string|null>(null);
  useEffect(()=>{dtRef.current=dt},[dt]);
  useEffect(()=>{dbRef.current=db},[db]);

  useEffect(()=>{const s=[...project.buckets].sort((a,b)=>a.orderHint>b.orderHint?-1:1);sBo(s.map(b=>b.id))},[project.buckets]);
  const ob=bo.map(id=>project.buckets.find(b=>b.id===id)).filter(Boolean) as Bucket[];

  // Lokale task-state voor optimistische updates (zonder API-wachttijd)
  const[localTasks,setLocalTasks]=useState<PlannerTask[]|null>(null);
  const effectiveTasks=localTasks??project.tasks;

  // Afvinken doorvoeren in de bord-state: de taak verhuist direct tussen
  // de actieve kolom en het groepje "Voltooide taken". De API-call zelf
  // gebeurt in TaskCard; dit is alleen de optimistische verplaatsing.
  const setTaskDone=(taskId:string,doneNow:boolean)=>{
    setLocalTasks(prev=>{
      const b=prev??project.tasks;
      return b.map(t=>t.id===taskId?{...t,percentComplete:doneNow?100:0}:t);
    });
  };
  // Reset lokale state ALLEEN als de set taken (id's of buckets) verandert.
  // Niet bij checklist-updates, want die wijzigen alleen het checklist-veld.
  // Hierdoor blijft een drag-and-drop niet halverwege geannuleerd door fase-2 checklist-laden.
  const taskKeyRef=useRef("");
  useEffect(()=>{
    const key=project.tasks.map(t=>`${t.id}:${t.bucketId}`).sort().join(",");
    if(key!==taskKeyRef.current){
      taskKeyRef.current=key;
      setLocalTasks(null);
    }
  },[project.tasks]);

  // Drop-positie state voor herordening binnen een bucket.
  // dropAt = "above:taskId" of "below:taskId" — de plek waar een ingesleept item neerkomt.
  const[dropAt,setDropAt]=useState<string|null>(null);
  const dropAtRef=useRef<string|null>(null);
  useEffect(()=>{dropAtRef.current=dropAt},[dropAt]);

  // Safety-net: als een drag mislukt of geannuleerd wordt, vuren onDragEnd-handlers
  // soms niet af. Reset state altijd op een global dragend op het document.
  useEffect(()=>{
    const reset=()=>{
      sDt(null);dtRef.current=null;
      sDob(null);
      sDb(null);dbRef.current=null;
      setDropAt(null);
    };
    document.addEventListener("dragend",reset);
    return()=>{document.removeEventListener("dragend",reset);};
  },[]);

  // Sleep een taak naar een specifieke positie in een bucket.
  // Bepaalt nieuwe orderHint op basis van de buren en stuurt PATCH naar Planner.
  const reorderTaskAtPosition=async(bucketId:string, beforeTaskId:string|null, afterTaskId:string|null)=>{
    const task=dtRef.current;
    if(!task||mv)return;

    const base=localTasks??project.tasks;

    // Zoek de actieve taken in de doelbucket op huidige sorteervolgorde,
    // zonder de gesleepte taak (die wordt straks op de nieuwe plek gezet).
    const bucketTasks=base
      .filter(t=>t.bucketId===bucketId && t.id!==task.id && t.percentComplete<100)
      .sort((a,b)=>a.orderHint<b.orderHint?-1:1);

    // Bepaal de buren waar de taak tussenkomt
    let before:PlannerTask|null=null, after:PlannerTask|null=null;
    if(beforeTaskId){
      const idx=bucketTasks.findIndex(t=>t.id===beforeTaskId);
      if(idx>=0){
        before=bucketTasks[idx];
        after=bucketTasks[idx+1]||null;
      }
    } else if(afterTaskId){
      const idx=bucketTasks.findIndex(t=>t.id===afterTaskId);
      if(idx>=0){
        before=bucketTasks[idx-1]||null;
        after=bucketTasks[idx];
      }
    } else {
      // Geen buren opgegeven: aan het einde plakken
      before=bucketTasks[bucketTasks.length-1]||null;
      after=null;
    }

    // Als de taak al precies op deze plek staat, niets doen
    if(task.bucketId===bucketId){
      const currentIdx=base.filter(t=>t.bucketId===bucketId && t.percentComplete<100)
        .sort((a,b)=>a.orderHint<b.orderHint?-1:1)
        .findIndex(t=>t.id===task.id);
      const targetIdx=before?bucketTasks.findIndex(t=>t.id===before!.id)+1:0;
      if(currentIdx===targetIdx){sDt(null);dtRef.current=null;sDob(null);setDropAt(null);return;}
    }

    // De hint die we naar Planner sturen: "between"-syntax die de server zelf oplost
    const serverHint=buildOrderHint(before?.orderHint||null, after?.orderHint||null);

    // Voor de optimistische UI hebben we een lokale sorteerwaarde nodig.
    // Planner-hints zijn lexicografisch sorteerbaar, dus we kunnen tijdelijk
    // iets gebruiken dat tussen de buren valt. We pakken eenvoudig de hint van
    // 'before' + " A" als suffix — dat is groter dan before, en typisch kleiner
    // dan after (after begint met dezelfde digits + andere suffix).
    let localHint:string;
    if(before && after) localHint = before.orderHint + " A!";
    else if(before) localHint = before.orderHint + " A!";
    else if(after) localHint = " " + after.orderHint;
    else localHint = " !";

    // Optimistische update: zet bucketId en lokale orderHint
    setLocalTasks(prev=>{
      const b=prev??project.tasks;
      return b.map(t=>t.id===task.id?{...t,bucketId:bucketId,orderHint:localHint}:t);
    });

    sMv(true);
    try{
      // Eerst etag ophalen, dan patchen
      const d=await gQ(()=>graphClient.api(`/planner/tasks/${task.id}`).get());
      const payload:any={orderHint:serverHint};
      if(task.bucketId!==bucketId)payload.bucketId=bucketId;
      await gQ(()=>graphClient.api(`/planner/tasks/${task.id}`).header("If-Match",d["@odata.etag"]).patch(payload));
    }catch(e){
      console.error("Volgorde wijzigen mislukt:",e);
      setLocalTasks(null); // terugdraaien
    }
    sMv(false);sDt(null);dtRef.current=null;sDob(null);setDropAt(null);
  };

  const dropTask=async(bid:string)=>{
    const task=dtRef.current;
    if(!task||mv){sDt(null);dtRef.current=null;sDob(null);setDropAt(null);return}

    // Als er een precieze drop-positie is, gebruik die voor herordening
    if(dropAtRef.current){
      const[pos,tid]=dropAtRef.current.split(":");
      if(pos==="above")return reorderTaskAtPosition(bid,null,tid);
      if(pos==="below")return reorderTaskAtPosition(bid,tid,null);
    }

    // Anders: alleen bucket wisselen (oude gedrag), aan het einde plakken
    if(task.bucketId===bid){sDt(null);dtRef.current=null;sDob(null);return}
    return reorderTaskAtPosition(bid,null,null);
  };

  const reorderBucket=(dragId:string,overId:string)=>{
    if(dragId===overId)return;
    sBo(p=>{
      const f=p.indexOf(dragId),t=p.indexOf(overId);
      if(f===-1||t===-1)return p;
      const n=[...p];n.splice(f,1);n.splice(t,0,dragId);return n;
    });
  };

  const ha=async(bid:string)=>{
    if(!nt.trim())return;
    sAl(true);
    try{
      await gQ(()=>graphClient.api("/planner/tasks").post({planId:project.plan.id,bucketId:bid,title:nt.trim()}));
      sNt("");sAd(null);
      // Wel onRefresh: nieuwe taak bestaat alleen op de server, hij moet binnenkomen via fetch
      onRefresh();
    }catch(e){console.error(e)}
    sAl(false);
  };

  return <div style={{display:"flex",gap:10,overflowX:"auto",flex:1,minHeight:0}}>
    {ob.map(bucket=>{
      const all=effectiveTasks.filter(t=>t.bucketId===bucket.id);
      // Sorteer actieve taken op orderHint (lexicografisch oplopend = bovenaan eerst)
      const act=all.filter(t=>t.percentComplete<100).sort((a,b)=>a.orderHint<b.orderHint?-1:1);
      const cmp=all.filter(t=>t.percentComplete===100);
      const isTaskTarget=dob===bucket.id&&dt!=null;
      const isBucketDragging=db===bucket.id;

      return (
        <div
          key={bucket.id}
          // Bucket-container: NIET draggable. Alleen drop-target voor taken.
          onDragOver={e=>{
            e.preventDefault();
            // Alleen reageren als een TAAK gesleept wordt, niet een bucket
            if(dtRef.current){
              e.dataTransfer.dropEffect="move";
              sDob(bucket.id);
              if(!dt)sDt(dtRef.current);
            }
          }}
          onDragLeave={e=>{
            // Verlaat de bucket-container: verwijder highlight alleen als we echt buiten zijn
            if(!e.currentTarget.contains(e.relatedTarget as Node)){
              sDob(prev=>prev===bucket.id?null:prev);
              setDropAt(null);
            }
          }}
          onDrop={e=>{
            e.preventDefault();
            if(dtRef.current){
              dropTask(bucket.id);
            } else if(dbRef.current && dbRef.current!==bucket.id){
              reorderBucket(dbRef.current,bucket.id);
              sDb(null);dbRef.current=null;
            }
          }}
          style={{
            minWidth:220,maxWidth:300,flex:"1 1 220px",
            display:"flex",flexDirection:"column",
            background:"#FAFAFA",borderRadius:8,
            border:isTaskTarget?`2px solid ${project.color}`:"1px solid #E8E8E8",
            overflow:"hidden",
            opacity:isBucketDragging?.4:1,
            transition:"border-color 0.1s, opacity 0.15s",
          }}
        >
          {/* ── Bucket-header: dit deel is draggable voor herordening ── */}
          <div
            draggable
            onDragStart={e=>{
              // Alleen bucket-herordening starten als er GEEN taak actief is
              if(dtRef.current){e.preventDefault();return;}
              sDb(bucket.id);dbRef.current=bucket.id;
              e.dataTransfer.effectAllowed="move";
              e.dataTransfer.setData("bucketId",bucket.id);
            }}
            onDragEnd={()=>{sDb(null);dbRef.current=null;}}
            onDragOver={e=>{
              e.preventDefault();e.stopPropagation();
              // Bucket over andere bucket-header: herorden
              if(!dtRef.current && dbRef.current && dbRef.current!==bucket.id){
                reorderBucket(dbRef.current,bucket.id);
              }
            }}
            style={{padding:"8px 10px",borderBottom:"1px solid #E8E8E8",background:isTaskTarget?project.color+"08":"#F5F5F5",cursor:"grab",userSelect:"none",display:"flex",alignItems:"center",gap:6}}
          >
            <span style={{opacity:.4}}><I.Grip/></span>
            <span style={{fontSize:12,fontWeight:700,color:"#333",flex:1}}>{bucket.name}</span>
            <span style={{fontSize:10,color:"#9AA0A6",fontWeight:600}}>{act.length}</span>
          </div>

          {/* ── Taak-lijst ── */}
          <div style={{flex:1,overflowY:"auto",padding:8,display:"flex",flexDirection:"column",gap:5}}>
            {ad===bucket.id
              ?<div style={{background:"#fff",borderRadius:5,padding:8,border:`1px solid ${project.color}`,marginBottom:2}}>
                <input autoFocus value={nt} onChange={e=>sNt(e.target.value)} placeholder="Taaknaam..." style={{width:"100%",border:"none",outline:"none",fontSize:12,background:"transparent",color:"#1A1A1A",fontFamily:"Segoe UI"}} onKeyDown={e=>{if(e.key==="Escape"){sAd(null);sNt("")}if(e.key==="Enter")ha(bucket.id)}}/>
                <div style={{display:"flex",gap:4,marginTop:6}}>
                  <button onClick={()=>{sAd(null);sNt("")}} style={{fontSize:10,padding:"3px 8px",borderRadius:4,border:"1px solid #E0E0E0",background:"#F0F0F0",color:"#9AA0A6",cursor:"pointer",fontFamily:"Segoe UI"}}>Annuleren</button>
                  <button onClick={()=>ha(bucket.id)} disabled={al} style={{fontSize:10,padding:"3px 8px",borderRadius:4,border:"none",background:project.color,color:"#fff",cursor:"pointer",fontWeight:600,fontFamily:"Segoe UI"}}>{al?"...":"Toevoegen"}</button>
                </div>
              </div>
              :<button onClick={()=>{sAd(bucket.id);sNt("")}} style={{display:"flex",alignItems:"center",gap:4,fontSize:11,color:"#9AA0A6",background:"none",border:"none",padding:"3px 0",cursor:"pointer",fontFamily:"Segoe UI"}}><I.Plus/> Taak toevoegen</button>
            }
            {(() => {
              // Strategie:
              // - De gesleepte taak BLIJFT in de lijst staan, anders verliest de browser
              //   de drag-source en stopt de drag-operatie meteen.
              // - Op de plek waar de taak terechtkomt tonen we een ghost slot.
              // - De gesleepte taak zelf wordt visueel vervangen door een ghost (zelfde
              //   grijze vakje), zodat het lijkt alsof hij verplaatst is.
              type Slot={kind:"task";task:PlannerTask}|{kind:"ghost";id:string};
              const dragId=dt?.id||null;
              const isTargetBucket=dob===bucket.id && dragId;

              // Bepaal waar de ghost komt (de drop-positie)
              let ghostBeforeId:string|null=null; // ghost komt VOOR deze task-id (in act-volgorde, exclusief de gesleepte)
              let ghostAtEnd=false;
              if(isTargetBucket){
                if(dropAt){
                  const[pos,tid]=dropAt.split(":");
                  if(pos==="above")ghostBeforeId=tid;
                  else if(pos==="below"){
                    const visible=act.filter(t=>t.id!==dragId);
                    const idx=visible.findIndex(t=>t.id===tid);
                    if(idx>=0 && idx<visible.length-1) ghostBeforeId=visible[idx+1].id;
                    else ghostAtEnd=true;
                  }
                } else {
                  ghostAtEnd=true;
                }
              }

              const slots:Slot[]=[];
              for(const t of act){
                // Plek voor de drop-ghost?
                if(isTargetBucket && t.id===ghostBeforeId && t.id!==dragId){
                  slots.push({kind:"ghost",id:"drop"});
                }
                if(t.id===dragId){
                  // Vervang de gesleepte taak ter plekke door een ghost.
                  // Hierdoor lijkt het of hij is opgepakt en zweeft.
                  // ALLEEN als deze bucket NIET het target is (anders zou er een dubbele ghost staan)
                  // of als we per se aan het einde droppen.
                  if(!isTargetBucket){
                    slots.push({kind:"ghost",id:"source"});
                  } else {
                    // Target = deze bucket: laat geen extra source-ghost zien,
                    // de drop-ghost is genoeg. We slaan de taak hier dus over.
                  }
                } else {
                  slots.push({kind:"task",task:t});
                }
              }
              if(isTargetBucket && ghostAtEnd){
                slots.push({kind:"ghost",id:"drop"});
              }
              // Lege bucket of bucket zonder gewone taken: voeg alsnog een ghost toe als deze het target is
              const hasGhost=slots.some((s:Slot)=>s.kind==="ghost");
              if(isTargetBucket && !hasGhost){
                slots.push({kind:"ghost",id:"drop"});
              }

              return slots.map((slot,si)=>{
                if(slot.kind==="ghost"){
                  return <div
                    key={`ghost-${slot.id}-${si}`}
                    onDragOver={e=>{
                      if(!dtRef.current)return;
                      e.preventDefault();
                      e.stopPropagation();
                      e.dataTransfer.dropEffect="move";
                    }}
                    onDrop={e=>{
                      if(!dtRef.current)return;
                      e.preventDefault();
                      e.stopPropagation();
                      dropTask(bucket.id);
                    }}
                    style={{
                      height:42,
                      background:"#E8E8E8",
                      borderRadius:6,
                      border:"1px dashed #B8B8B8",
                    }}
                  />;
                }
                const task=slot.task;
                const isThisDragging=dragId===task.id;
                return <div
                  key={task.id}
                  draggable
                  onDragStart={e=>{
                    e.dataTransfer.effectAllowed="move";
                    e.dataTransfer.setData("taskId",task.id);
                    // Alleen refs zetten — GEEN state-update tijdens dragstart,
                    // want een rerender breekt de drag-source af in React.
                    // De state wordt bij het eerste dragover-event geüpdatet,
                    // wanneer de browser de drag al heeft geaccepteerd.
                    dtRef.current=task;
                    dbRef.current=null;
                  }}
                  onDragEnd={()=>{
                    sDt(null);dtRef.current=null;sDob(null);setDropAt(null);
                  }}
                  onDragOver={e=>{
                    if(!dtRef.current||dtRef.current.id===task.id)return;
                    e.preventDefault();
                    e.stopPropagation();
                    e.dataTransfer.dropEffect="move";
                    const rect=e.currentTarget.getBoundingClientRect();
                    const half=rect.top+rect.height/2;
                    sDob(bucket.id);
                    setDropAt(e.clientY<half?`above:${task.id}`:`below:${task.id}`);
                    // Sync de dt-state met de ref als hij nog niet gezet is
                    if(!dt)sDt(dtRef.current);
                  }}
                  onDrop={e=>{
                    if(!dtRef.current)return;
                    e.preventDefault();
                    e.stopPropagation();
                    dropTask(bucket.id);
                  }}
                  style={{
                    cursor:mv?"wait":"grab",
                    // Gesleepte taak: lichtjes vervagen zodat duidelijk is dat hij wordt
                    // verplaatst. NIET inklappen of verbergen — dat breekt de native drag.
                    opacity:isThisDragging?0.3:1,
                  }}
                >
                  <TaskCard task={task} color={project.color} done={false} graphClient={graphClient} onRefresh={onRefresh} onToggleDone={setTaskDone}/>
                </div>;
              });
            })()}
            {cmp.length>0&&(
              <div style={{marginTop:4}}>
                <button onClick={()=>sSc(p=>({...p,[bucket.id]:!p[bucket.id]}))} style={{display:"flex",alignItems:"center",gap:4,fontSize:10,color:"#9AA0A6",background:"none",border:"none",cursor:"pointer",fontFamily:"Segoe UI",padding:"3px 0",fontWeight:600}}>
                  {sc[bucket.id]?<I.ChevDown/>:<I.ChevRight/>} Voltooide taken ({cmp.length})
                </button>
                {sc[bucket.id]&&<div style={{display:"flex",flexDirection:"column",gap:4,marginTop:4}}>{cmp.map(t=><TaskCard key={t.id} task={t} color={project.color} done={true} graphClient={graphClient} onRefresh={onRefresh} onToggleDone={setTaskDone}/>)}</div>}
              </div>
            )}
          </div>
        </div>
      );
    })}
  </div>
}
